export interface WaiveReason {
  value: string;
  label: string;
}

export const WAIVE_REASONS: WaiveReason[] = [
  { value: "spouse_or_dp_plan",        label: "Covered by spouse/domestic partner's employer plan" },
  { value: "parent_plan",              label: "Covered by a parent's plan" },
  { value: "medicare",                 label: "Coverage through Medicare" },
  { value: "medicaid",                 label: "Coverage through Medicaid / Medi-Cal" },
  { value: "tricare_military_va",      label: "Coverage through TRICARE / military / VA benefits" },
  { value: "government_or_union_plan", label: "Coverage through a government/union plan" },
  { value: "individual_plan",          label: "Coverage through individual plan" },
  { value: "cobra",                    label: "Coverage through COBRA" },
  { value: "other_group_plan",         label: "Coverage through another group plan" },
  { value: "religious_objection",      label: "Religious objection" },
  { value: "cost",                     label: "Decline due to cost" },
  { value: "voluntary_waiver",         label: "Voluntary waiver" },
  { value: "other",                    label: "Other" },
];

export const LEGACY_WAIVE_REASON_VALUES = [
  "switched_to_spouse_plan",
  "covered_under_parent",
  "covered_elsewhere",
  "personal_choice",
  "other_coverage",
] as const;

export const NON_WAIVER_EVENT_TYPES = [
  "qualifying_life_event",
  "cobra_conversion",
] as const;

export const ACCEPTED_EVENT_TYPES: ReadonlySet<string> = new Set<string>([
  ...WAIVE_REASONS.map((r) => r.value),
  ...LEGACY_WAIVE_REASON_VALUES,
  ...NON_WAIVER_EVENT_TYPES,
]);

export const LEGACY_TO_NEW: Record<string, string> = {
  switched_to_spouse_plan: "spouse_or_dp_plan",
  covered_under_parent:    "parent_plan",
  covered_elsewhere:       "other_group_plan",
  personal_choice:         "voluntary_waiver",
  other_coverage:          "other_group_plan",
};

export function getWaiveReasonLabel(value: string | null | undefined): string {
  if (!value) return "—";
  const normalized = LEGACY_TO_NEW[value] ?? value;
  const hit = WAIVE_REASONS.find((r) => r.value === normalized);
  if (hit) return hit.label;
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-dependent waive-reason adapter
//
// The server stores `enrollments.dependentWaiverReasons` as a flat
// `Record<depId, string>` mixing canonical slugs and freeform text. UI surfaces
// that use the `DependentsBody` component need a two-map shape — one for the
// dropdown selection, one for the freeform "Other" text. These helpers convert
// between the two formats so every surface (modal, wizard, broker Configure
// form) shares one implementation.
// ─────────────────────────────────────────────────────────────────────────────

const KNOWN_WAIVE_SLUGS = new Set<string>(WAIVE_REASONS.map((r) => r.value));

/**
 * Split a stored waive-reason string into the dropdown selection + freeform
 * text. Empty input ⇒ no decision. A canonical slug (or legacy alias) ⇒ that
 * slug. Anything else ⇒ `reason="other"` with the original as `otherText`.
 */
export function classifyDepReason(
  stored: string | null | undefined,
): { reason: string; otherText: string } {
  const v = (stored ?? "").trim();
  if (!v) return { reason: "", otherText: "" };
  const normalized = LEGACY_TO_NEW[v] ?? v;
  if (KNOWN_WAIVE_SLUGS.has(normalized)) return { reason: normalized, otherText: "" };
  return { reason: "other", otherText: v };
}

/**
 * Inverse of `classifyDepReason`. Combines the two-map shape back into the
 * server's flat `Record<depId, string>` wire format. Skips empty entries;
 * `reason==="other"` writes the trimmed `otherText` (or skips if blank).
 * Returns `null` when no usable entries exist.
 */
export function flattenDepReasons(
  reasonMap: Record<number, string> | undefined,
  otherTextMap: Record<number, string> | undefined,
): Record<string, string> | null {
  if (!reasonMap || Object.keys(reasonMap).length === 0) return null;
  const out: Record<string, string> = {};
  for (const [id, reason] of Object.entries(reasonMap)) {
    if (!reason) continue;
    if (reason === "other") {
      const txt = (otherTextMap?.[Number(id)] ?? "").trim();
      if (txt) out[id] = txt;
    } else {
      out[id] = reason;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}
